import { api } from "@/lib/api";
import type { Page } from "@/features/users/api";

export type ProductStatus = "active" | "discontinued";
export type MovementType =
  | "purchase_in"
  | "dispatch_out"
  | "adjustment"
  | "rma_out"
  | "rma_in";
export type PoStatus =
  | "draft"
  | "ordered"
  | "partially_received"
  | "received"
  | "cancelled";
export type RmaStatus =
  | "requested"
  | "sent"
  | "replaced"
  | "refunded"
  | "rejected"
  | "closed";

// --- vendors ---

export interface Vendor {
  id: string;
  code: string;
  name: string;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
}

export interface VendorDetail extends Vendor {
  address: string | null;
  gst_number: string | null;
  notes: string | null;
  created_at: string;
}

export interface VendorBody {
  name: string;
  contact_name?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  gst_number?: string | null;
  notes?: string | null;
}

// --- products ---

export interface Product {
  id: string;
  sku: string;
  barcode: string | null;
  name: string;
  category: string | null;
  unit: string;
  unit_cost: string | null;
  sale_price: string | null;
  quantity_on_hand: number;
  reorder_level: number;
  low_stock: boolean;
  status: ProductStatus;
  preferred_vendor_id: string | null;
  preferred_vendor_name: string | null;
}

export interface ProductDetail extends Product {
  description: string | null;
  created_at: string;
}

export interface ProductBody {
  name: string;
  sku?: string | null;
  barcode?: string | null;
  category?: string | null;
  description?: string | null;
  unit?: string;
  unit_cost?: string | null;
  sale_price?: string | null;
  reorder_level?: number;
  status?: ProductStatus;
  preferred_vendor_id?: string | null;
  opening_stock?: number;
}

export interface StockMovement {
  id: string;
  product_id: string;
  movement_type: MovementType;
  quantity: number;
  unit_cost: string | null;
  reference_type: string | null;
  reference_id: string | null;
  note: string | null;
  created_at: string;
}

// --- purchase orders ---

export interface PoLine {
  id: string;
  product_id: string;
  product_name: string | null;
  product_sku: string | null;
  quantity_ordered: number;
  quantity_received: number;
  unit_cost: string | null;
}

export interface PurchaseOrder {
  id: string;
  po_number: string;
  vendor_id: string;
  vendor_name: string | null;
  status: PoStatus;
  order_date: string | null;
  expected_date: string | null;
  line_count: number;
  total_cost: string;
}

export interface PurchaseOrderDetail extends PurchaseOrder {
  notes: string | null;
  lines: PoLine[];
  created_at: string;
}

export interface PoLineInput {
  product_id: string;
  quantity_ordered: number;
  unit_cost?: string | null;
}

export interface PurchaseOrderBody {
  vendor_id: string;
  order_date?: string | null;
  expected_date?: string | null;
  notes?: string | null;
  lines: PoLineInput[];
}

// --- RMAs ---

export interface Rma {
  id: string;
  rma_number: string;
  product_id: string;
  product_name: string | null;
  vendor_id: string | null;
  vendor_name: string | null;
  ticket_id: string | null;
  quantity: number;
  status: RmaStatus;
}

export interface RmaDetail extends Rma {
  serial_number: string | null;
  reason: string;
  resolution_note: string | null;
  created_at: string;
}

export interface RmaBody {
  product_id: string;
  vendor_id?: string | null;
  ticket_id?: string | null;
  quantity?: number;
  serial_number?: string | null;
  reason: string;
}

function qs(filters: object): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== null && v !== "" && v !== false) params.set(k, String(v));
  }
  const s = params.toString();
  return s ? `?${s}` : "";
}

// --- vendor calls ---
export const listVendors = (f: object = {}) => api.get<Page<Vendor>>(`/vendors${qs(f)}`);
export const createVendor = (b: VendorBody) => api.post<VendorDetail>("/vendors", b);
export const updateVendor = (id: string, b: Partial<VendorBody>) =>
  api.patch<VendorDetail>(`/vendors/${id}`, b);
export const deleteVendor = (id: string) => api.delete<{ message: string }>(`/vendors/${id}`);

// --- product calls ---
export const listProducts = (f: object = {}) => api.get<Page<Product>>(`/products${qs(f)}`);
export const getProduct = (id: string) => api.get<ProductDetail>(`/products/${id}`);
export const productByBarcode = (code: string) =>
  api.get<ProductDetail>(`/products/by-barcode/${encodeURIComponent(code)}`);
export const createProduct = (b: ProductBody) => api.post<ProductDetail>("/products", b);
export const updateProduct = (id: string, b: Partial<ProductBody>) =>
  api.patch<ProductDetail>(`/products/${id}`, b);
export const deleteProduct = (id: string) => api.delete<{ message: string }>(`/products/${id}`);
export const adjustStock = (id: string, quantity: number, note?: string) =>
  api.post<ProductDetail>(`/products/${id}/adjust`, { quantity, note: note ?? null });
export const productMovements = (id: string) =>
  api.get<StockMovement[]>(`/products/${id}/movements`);

// --- purchase order calls ---
export const listPos = (f: object = {}) => api.get<Page<PurchaseOrder>>(`/purchase-orders${qs(f)}`);
export const getPo = (id: string) => api.get<PurchaseOrderDetail>(`/purchase-orders/${id}`);
export const createPo = (b: PurchaseOrderBody) =>
  api.post<PurchaseOrderDetail>("/purchase-orders", b);
export const submitPo = (id: string) =>
  api.post<PurchaseOrderDetail>(`/purchase-orders/${id}/submit`);
export const receivePo = (id: string, lines?: { product_id: string; quantity: number }[]) =>
  api.post<PurchaseOrderDetail>(`/purchase-orders/${id}/receive`, { lines: lines ?? null });
export const cancelPo = (id: string) =>
  api.post<PurchaseOrderDetail>(`/purchase-orders/${id}/cancel`);

// --- RMA calls ---
export const listRmas = (f: object = {}) => api.get<Page<Rma>>(`/rmas${qs(f)}`);
export const getRma = (id: string) => api.get<RmaDetail>(`/rmas/${id}`);
export const createRma = (b: RmaBody) => api.post<RmaDetail>("/rmas", b);
export const updateRma = (
  id: string,
  b: { status?: RmaStatus; vendor_id?: string | null; resolution_note?: string | null },
) => api.patch<RmaDetail>(`/rmas/${id}`, b);
